import {
  AccountScreen, AppShell, CalendarList, EmptyState, EntryOrNotFound, entryRoutes, Messaging, NotificationCentre,
  Permissions, RequirePermission, RequireSession, useI18n, useSession, useTenantSettings,
  useUnreadConversations, type NavItem,
} from '@daghfal/core';
import { Route, Routes } from 'react-router-dom';
import { QueueProvider, useQueueEntries } from './offline/QueueProvider';
import { Attendance } from './routes/Attendance';
import { MedicationRound } from './routes/MedicationRound';
import { MyClass } from './routes/MyClass';
import { MyPhotos } from './routes/MyPhotos';
import { PhotoUpload } from './routes/PhotoUpload';
import { GroupEntry, QuickEntry } from './routes/QuickEntry';
import { Queue } from './routes/Queue';
import { RecordIncident } from './routes/RecordIncident';
import { STAFF_ROUTES } from './routeMarks';

export function App() {
  const { t } = useI18n();
  const brand = { name: t('app.name'), tagline: t('app.subtitle') };

  return (
    <Routes>
      {entryRoutes(brand, { slugs: true })}
      <Route
        path="/*"
        element={
          <RequireSession slugEntry>
            {/* The queue lives above the routes so that entries keep flushing while a teacher moves
                between screens, and the pending count is the same one everywhere (FR-STF-09). */}
            <QueueProvider><StaffShell /></QueueProvider>
          </RequireSession>
        }
      />
    </Routes>
  );
}

function StaffShell() {
  const { t } = useI18n();
  const { session, can } = useSession();
  const pending = useQueueEntries();
  // An assistant cannot message, so asking for their conversations would only be refused.
  const unreadConversations = useUnreadConversations(can(Permissions.messages.direct));
  useTenantSettings();

  const nav: NavItem[] = [
    { to: '/', label: t('nav.class'), icon: '🧒', end: true },
    { to: '/attendance', label: t('nav.attendance'), icon: '✅', permission: Permissions.attendance.record },
    {
      to: '/medications', label: t('nav.medications'), icon: '💊',
      permission: Permissions.health.administerMedication,
    },
    {
      to: '/messages', label: t('nav.messages'), icon: '💬', permission: Permissions.messages.direct,
      count: unreadConversations,
    },
    { to: '/photos', label: t('nav.photos'), icon: '📷', permission: Permissions.photos.upload },
    // Every member reads the calendar: a closure or a trip is the whole staff's to know.
    { to: '/calendar', label: t('nav.calendar'), icon: '🗓️' },
    // The badge is the requirement's "the user shall be told when items are pending", made permanent
    // rather than a toast that can be missed.
    { to: '/queue', label: t('nav.queue'), icon: '📤', count: pending.length },
  ];

  const nursery = session?.membership.tenantNameAr ?? t('app.name');

  return (
    <AppShell
      brand={nursery} context={session?.membership.roleNameAr} nav={nav} mobile
      notificationsPath="/notifications" watermarkRoutes={STAFF_ROUTES}
    >
      <Routes>
        <Route index element={<MyClass />} />
        <Route
          path="child/:childId"
          element={
            <RequirePermission all={[Permissions.daily.record]}>
              <QuickEntry />
            </RequirePermission>
          }
        />
        <Route
          path="child/:childId/incident"
          element={
            <RequirePermission all={[Permissions.health.createIncident]}>
              <RecordIncident />
            </RequirePermission>
          }
        />
        <Route
          path="medications"
          element={
            <RequirePermission all={[Permissions.health.administerMedication]}>
              <MedicationRound />
            </RequirePermission>
          }
        />
        <Route
          path="group"
          element={
            <RequirePermission all={[Permissions.daily.record]}>
              <GroupEntry />
            </RequirePermission>
          }
        />
        <Route
          path="attendance"
          element={
            <RequirePermission all={[Permissions.attendance.record]}>
              <Attendance />
            </RequirePermission>
          }
        />
        <Route
          path="messages"
          element={
            <RequirePermission all={[Permissions.messages.direct]}>
              <Messaging />
            </RequirePermission>
          }
        />
        <Route
          path="photos"
          element={
            <RequirePermission all={[Permissions.photos.upload]}>
              <MyPhotos />
            </RequirePermission>
          }
        />
        <Route
          path="child/:childId/photo"
          element={
            <RequirePermission all={[Permissions.photos.upload]}>
              <PhotoUpload />
            </RequirePermission>
          }
        />
        <Route path="calendar" element={<CalendarList />} />
        <Route path="notifications" element={<NotificationCentre />} />
        <Route path="account" element={<AccountScreen />} />
        <Route path="queue" element={<Queue />} />
        <Route path="*" element={<EntryOrNotFound><EmptyState icon="🔍" title={t('error.notFound')} /></EntryOrNotFound>} />
      </Routes>
    </AppShell>
  );
}
